import moment from 'moment';
import {
  each, values, sortBy, sumBy, indexOf,
  uniq, map, some, findIndex, reduce, find,
} from 'lodash';

import { ADD_FORECAST_OPPORTUNITIES, FETCH_FORECAST_PERIODS,
  ADD_FORECAST_OPPORTUNITY_LOGS,
  ADD_FORECAST_HISTORY_OPPORTUNITIES } from './ForecastActions';

import { formatRange } from '../../util/helpers';
import { DASHBOARD_FETCH_WIN_LOSS_OPPORTUNITY_REQUESTED_FEEDBACK } from '../Dashboard/DashboardActions';

// Initial State
const initialState = {
  winRates: {},
  ptdWins: {},

  useForecastAccuracies: false,
  forecastAccuracies: {},

  forecasts: [],
  beginningStats: {},

  currentOpportunities: [],
  currentStats: {},

  slippedOpportunities: [],
  slippedStats: {},

  newOpportunities: [],
  newStats: {},

  wonOpportunities: [],
  wonStats: {},

  lostOpportunities: [],
  lostStats: {},

  noDecisionOpportunities: [],
  noDecisionStats: {},

  closedOpportunities: [],
  closedStats: {},

  status: null,
  errors: null,
  stats: [],
  periodCurrent: 0, // current days
  periodTotal: 0, // total days
  periodRange: '',
  periodStart: null,
  periodEnd: null,
  syncDate: null,
  forecastId: null,
  isFutureForecast: false,
  periods: [],

  historyForecasts: [],
  selectedPeriod: null,
  historyStatus: null,
  historyErrors: null,
};

function getDealStats(opportunities, isBeginning = false, isWeighted = false) {
  const colors = {
    dealTypes: ['#9575CD', '#673AB7', '#512DA8', '#311B92', '#cccccc'],
    forecasts: ['#0D47A1', '#1565C0', '#1E88E5', '#42A5F5', '#90CAF9', '#E3F2FD'],
    sizes: ['#AED581', '#8BC34A', '#689F38'],
    cycles: ['#FFF176', '#FDD835', '#F57F17'],
  };

  const dealTypeLables = {
    'New business': 'New business',
    Renewal: 'Renewals',
    'Add-on': 'Addons',
    Upsell: 'Upsells',
    Others: 'Others',
  };
  const stats = {
    dealTypes: {},
    forecasts: {},
    sizes: {
      small: {
        label: 'Small Deals', legend: 'Small', count: 0, amount: 0, amountPercentage: 0, owners: [],
      },
      average: {
        label: 'Average Deals', legend: 'Average', count: 0, amount: 0, amountPercentage: 0, owners: [],
      },
      large: {
        label: 'Large Deals', legend: 'Large', count: 0, amount: 0, amountPercentage: 0, owners: [],
      },
    },
    cycles: {
      short: {
        label: 'Short Pursuits', legend: 'Short', count: 0, amount: 0, amountPercentage: 0, owners: [],
      }, // you can add color to chart here
      average: {
        label: 'Average Pursuits', legend: 'Average', count: 0, amount: 0, amountPercentage: 0, owners: [],
      },
      long: {
        label: 'Long Pursuits', legend: 'Long', count: 0, amount: 0, amountPercentage: 0, owners: [],
      },
    },
  };

  // const amountKey = isBeginning ? 'amount' : 'opportunity_amount';
  let amountKey = 'amount';
  if (isBeginning) {
    amountKey = 'amount';
  } else {
    amountKey = isWeighted ? '$adjustedAmount' : 'opportunity_amount';
  }

  // const forecastKey = isBeginning ? 'forecast' : 'opportunity_forecast';
  const cycleTimeKey = isBeginning ? 'cycle_time' : 'pursuit_length';
  const totalDeals = opportunities.length;
  const totalAmount = sumBy(opportunities, amountKey);
  const totalCycle = sumBy(opportunities, cycleTimeKey);

  const averageDealSize = totalDeals !== 0 ? totalAmount / totalDeals : 0;
  const amountLowerBound = averageDealSize - (averageDealSize * 0.15);
  const amountUpperBound = averageDealSize + (averageDealSize * 0.15);

  const averageCycle = totalDeals !== 0 ? totalCycle / totalDeals : 0;
  const cycleLowerBound = averageCycle - (averageCycle * 0.15);
  const cycleUpperBound = averageCycle + (averageCycle * 0.15);

  each(opportunities, (opportunity) => {
    const amount = opportunity[amountKey] || 0;
    const cycleTime = opportunity[cycleTimeKey] || 0;
    const owner = opportunity.owner_id;
    let dealTypes = opportunity.types || [];
    if (dealTypes.length === 0) {
      dealTypes = ['Others'];
    }

    each(dealTypes, (type) => {
      if (!stats.dealTypes[type]) {
        stats.dealTypes[type] = {
          count: 0,
          amount: 0,
          owners: [],
        };
      }
      stats.dealTypes[type].count += 1;
      stats.dealTypes[type].amount += amount;
      stats.dealTypes[type].owners.push(owner);
    });

    /*
    if (!stats.forecasts[opportunity[forecastKey]]) {
      stats.forecasts[opportunity[forecastKey]] = { count: 0, amount: 0, owners: [] };
    }
    stats.forecasts[opportunity[forecastKey]].count += 1;
    stats.forecasts[opportunity[forecastKey]].amount += amount;
    stats.forecasts[opportunity[forecastKey]].owners.push(owner);
    */
    let sizeKey = 'average';
    if (amount < amountLowerBound) {
      sizeKey = 'small';
    } else if (amount > amountUpperBound) {
      sizeKey = 'large';
    }
    stats.sizes[sizeKey].count += 1;
    stats.sizes[sizeKey].amount += amount;
    stats.sizes[sizeKey].owners.push(owner);

    let cycleKey = 'average';
    if (cycleTime < cycleLowerBound) {
      cycleKey = 'short';
    } else if (cycleTime > cycleUpperBound) {
      cycleKey = 'long';
    }
    stats.cycles[cycleKey].count += 1;
    stats.cycles[cycleKey].amount += amount;
    stats.cycles[cycleKey].owners.push(owner);
  });

  const orders = {
    cycles: ['short', 'average', 'long'],
    sizes: ['small', 'average', 'large'],
    // forecasts: ['Closed', 'Commit', 'Best Case', 'Pipeline', 'Omitted'],
    dealTypes: ['New business', 'Renewal', 'Add-on', 'Upsell', 'Others'],
  };
  each(orders, (types, statKey) => {
    const items = [];
    each(types, (type, idx) => {
      const count = stats[statKey][type] ? stats[statKey][type].count : 0;
      const amount = stats[statKey][type] ? stats[statKey][type].amount : 0;
      const owners = uniq(stats[statKey][type] ? stats[statKey][type].owners : []);

      if (count > 0) {
        let label = stats[statKey][type] ? (stats[statKey][type].label || type) : type;
        let labelAmount = null;
        let labelOp = null;
        if (statKey === 'dealTypes' && dealTypeLables[type]) {
          label = dealTypeLables[type];
        } else if (statKey === 'sizes') {
          if (type === 'small') {
            labelOp = '<';
            labelAmount = amountLowerBound;
          } else if (type === 'average') {
            labelOp = '<';
            labelAmount = amountUpperBound;
          } else if (type === 'large') {
            labelOp = '>';
            labelAmount = amountUpperBound;
          }
        } else if (statKey === 'cycles') {
          if (type === 'short') {
            labelOp = '<';
            labelAmount = cycleLowerBound;
          } else if (type === 'average') {
            labelOp = '<';
            labelAmount = cycleUpperBound;
          } else if (type === 'long') {
            labelOp = '>';
            labelAmount = cycleUpperBound;
          }
        }
        items.push({
          label,
          value: type,
          legend: stats[statKey][type] ? stats[statKey][type].legend : null,
          statKey,
          labelAmount,
          labelOp,
          color: colors[statKey][idx % colors[statKey].length] || undefined,
          count,
          amount,
          average: count !== 0 ? amount / count : 0,
          amountPercentage: 0,
          totalOwners: owners.length,
          totalAmount: 0,
        });
      }
    });
    const allAmount = sumBy(items, isWeighted ? '$adjustedAmount' : 'amount');
    each(items, (item) => {
      item.amountPercentage = allAmount !== 0 ? item.amount / allAmount : 0;
      item.totalAmount = allAmount;
    });
    stats[statKey] = items;
  });

  return stats;
}
function transformData(forecasts, ptdWins, forecastAccuracies, owners, untrackedOpportunities, isHistory = false, isWeighted = false) {
  const indexedStats = {};
  const closedOpportunities = [];
  const currentOpportunities = [];
  const slippedOpportunities = [];
  const noDecisionOpportunities = [];
  const lostOpportunities = [];
  const wonOpportunities = [];
  let useForecastAccuracies = false;
  const monthKeys = { three_months: 3, six_months: 6, twelve_months: 12 };
  const newForecastAccuracies = reduce(monthKeys, (items, accPeriod, monthKey) => {
    const mPeriod = find(forecastAccuracies, { accPeriod, accValid: true });
    if (mPeriod) {
      useForecastAccuracies = true;
      items[monthKey] = mPeriod.accRate;
    }
    return items;
  }, {});

  each(forecasts, (forecast) => {
    forecast.owner = owners[forecast.owner_id];
    let dealTypes = forecast.types || [];
    if (dealTypes.length === 0) {
      dealTypes = ['Others'];
    }



    // https://github.com/lumen-software/TruSales/issues/614
    // https://github.com/lumen-software/TruSales/issues/771#issuecomment-406330764
    // const isClosed = forecast.is_closed && !forecast.is_slipped;
    const isClosed = forecast.is_closed;
    const isCurrent = !forecast.is_closed && !forecast.is_slipped;

    forecast.$adjustedAmount = isCurrent ? (forecast.opportunity_amount || forecast.amount) : forecast.amount;
    
    if (isWeighted && !forecast.is_closed && forecast.weightedForecast !== null) {
      forecast.$adjustedAmount = forecast.weightedForecast;
      forecast.delta_amount = forecast.$adjustedAmount - forecast.amount;
    }



    // const isLost = isHistory ? (!forecast.is_slipped && forecast.stage === 'Lost' && forecast.is_closed) : (!forecast.is_slipped && !forecast.is_won && forecast.is_closed);
    const isLost = isHistory ? (forecast.stage === 'Lost' && forecast.is_closed) : (!forecast.is_won && forecast.is_closed);
    // const isNoDecision = (!forecast.is_slipped && forecast.stage === 'No Decision' && forecast.is_closed);
    const isNoDecision = (forecast.stage === 'No Decision' && forecast.is_closed);
    /*
    const isWon = isHistory ? (!forecast.is_slipped && forecast.stage === 'Won' && forecast.is_closed) :
      (forecast.stage === 'Won' && forecast.is_closed && (!forecast.is_slipped || forecast.opportunity_forecast === 'Closed'));
    */
    const isWon = forecast.stage === 'Won' && forecast.is_closed;
    // const isSlipped = forecast.is_slipped;
    const isSlipped = forecast.is_slipped && !forecast.is_closed;
    each(dealTypes, (type) => {
      if (!indexedStats[type]) {
        indexedStats[type] = {
          currentForecast: 0,
          PTDWins: ptdWins[type] ? ptdWins[type] : 0,
          beginningForecast: 0,
          new: 0,
          lost: 0,
          noDecision: 0,
          closedWon: 0, // forecast won + new won
          forecastWon: 0, // actual amount
          newWon: 0,
          newLost: 0,
          won: 0, // forecast won, forecast amount
          slipped: 0,
          accuracy: 0,
        };
      }
      if (isCurrent) {
        // indexedStats[type].currentForecast += forecast.opportunity_amount; // current amount, most recent data
        indexedStats[type].currentForecast += forecast.$adjustedAmount; // current amount, adjusted
        currentOpportunities.push(forecast);
      }

      if (isClosed) {
        indexedStats[type].closed += forecast.opportunity_amount; // final amount
        closedOpportunities.push(forecast);
      }

      if (isSlipped) {
        // amount should be adjusted
        // indexedStats[type].slipped += forecast.amount;
        indexedStats[type].slipped += forecast.$adjustedAmount;
        slippedOpportunities.push(forecast);
      }
      if (isLost) {
        // amount should be adjusted, the logic here is that we want to know the `forecast` lost
        // indexedStats[type].lost += forecast.amount;
        indexedStats[type].lost += forecast.$adjustedAmount;
        lostOpportunities.push(forecast);
      }
      if (isNoDecision) {
        // amount should be adjusted, the logic here is that we want to know the `forecast` lost
        // indexedStats[type].noDecision += forecast.amount;
        indexedStats[type].noDecision += forecast.$adjustedAmount;
        noDecisionOpportunities.push(forecast);
      }
      if (isWon) {
        // indexedStats[type].won += forecast.amount; // should be adjusted
        indexedStats[type].won += forecast.$adjustedAmount; // should be adjusted
        indexedStats[type].closedWon += forecast.opportunity_amount; // actual amount
        indexedStats[type].forecastWon += forecast.opportunity_amount; // actual amount
        wonOpportunities.push(forecast);
      }

      // indexedStats[type].beginningForecast += forecast.amount; // what being tracked, never change
      // https://github.com/lumen-software/TruSales/issues/956
      let bAmount = forecast.amount;
      if (isWeighted) {
        if (forecast.begingningWeightedForecast !== null) {
          bAmount = forecast.begingningWeightedForecast || 0;
        }
      }
      indexedStats[type].beginningForecast += bAmount;
    });
  });

  const openUntracked = [];
  const closedWonUntracked = [];
  const closedLostUntracked = [];
  const closedNoDecisionUntracked = [];
  const newOpportunities = untrackedOpportunities || [];

  each(untrackedOpportunities, (opportunity) => {
    opportunity.owner = owners[opportunity.owner_id];
    let dealTypes = opportunity.types || [];
    if (dealTypes.length === 0) {
      dealTypes = ['Others'];
    }

    opportunity.$adjustedAmount = opportunity.amount;
    if (isWeighted && !opportunity.is_closed && opportunity.weightedForecast !== null) {
      opportunity.$adjustedAmount = opportunity.weightedForecast;
      opportunity.delta_amount = opportunity.$adjustedAmount - opportunity.amount;
    }

    each(dealTypes, (type) => {
      if (!indexedStats[type]) {
        indexedStats[type] = {
          currentForecast: 0,
          PTDWins: ptdWins[type] ? ptdWins[type] : 0,
          beginningForecast: 0,
          new: 0,
          lost: 0,
          won: 0,
          noDecision: 0,
          closedWon: 0,
          forecastWon: 0, // actual amount
          newWon: 0,
          newLost: 0,
          slipped: 0,
          accuracy: 0,
        };
      }
      if (!opportunity.is_closed) {
        // indexedStats[type].currentForecast += opportunity.opportunity_amount; // current amount, most recent data
        indexedStats[type].currentForecast += opportunity.$adjustedAmount; // current amount, adjusted
        openUntracked.push(opportunity);
      }
      if (opportunity.is_closed && opportunity.is_won) {
        closedWonUntracked.push(opportunity);
        indexedStats[type].closedWon += opportunity.opportunity_amount; // actual amount
        indexedStats[type].newWon += opportunity.opportunity_amount; // actual amount
      }

      if (isHistory) {
        if (opportunity.is_closed && opportunity.stage === 'Lost') {
          closedLostUntracked.push(opportunity);
        }
        if (opportunity.is_closed && opportunity.stage === 'No Decision') {
          closedNoDecisionUntracked.push(opportunity);
        }
        if (opportunity.is_closed && !opportunity.is_won) {
          indexedStats[type].newLost += opportunity.opportunity_amount;
        }
      } else if (opportunity.is_closed && !opportunity.is_won) {
        closedLostUntracked.push(opportunity);
      }

      indexedStats[type].new += opportunity.$adjustedAmount; // should be adjusted if not closed
    });
  });

  each(ptdWins, (item, type) => {
    if (!indexedStats[type]) {
      indexedStats[type] = {
        currentForecast: 0,
        PTDWins: item,
        beginningForecast: 0,
        new: 0,
        lost: 0,
        won: 0,
        noDecision: 0,
        closedWon: 0,
        forecastWon: 0,
        slipped: 0,
        accuracy: 0,
      };
    }
  });

  each(indexedStats, (item, key) => {
    item.name = key;
    const won = isHistory ? item.closedWon : item.won;
    item.accuracy = item.beginningForecast > 0 ? won / item.beginningForecast : 0;
    item.paceVSForecast = item.currentForecast + item.PTDWins - item.beginningForecast;
    item.forecastChange = item.beginningForecast ? item.paceVSForecast / item.beginningForecast : 0;
  });

  const currentOpportunitiesWithUntrackedOpportunities = sortBy(currentOpportunities.concat(openUntracked || []), 'amount');
  const wonOpportunitiesWithUntrackedOpportunities = sortBy(wonOpportunities.concat(closedWonUntracked || []), 'amount');
  const lostOpportunitiesWithUntrackedOpportunities = isHistory ? lostOpportunities : sortBy(lostOpportunities.concat(closedLostUntracked || []), 'amount');
  const noDecisionOpportunitiesWithUntrackedOpportunities = isHistory ? noDecisionOpportunities : sortBy(noDecisionOpportunities.concat(closedNoDecisionUntracked || []), 'amount');

  const beginningStats = getDealStats(forecasts, true, isWeighted);
  const currentStats = getDealStats(currentOpportunitiesWithUntrackedOpportunities, false, isWeighted);

  const lostStats = getDealStats(lostOpportunitiesWithUntrackedOpportunities, false, isWeighted);

  const slippedStats = getDealStats(slippedOpportunities, false, isWeighted);
  const noDecisionStats = getDealStats(noDecisionOpportunitiesWithUntrackedOpportunities, false, isWeighted);
  // const closedStats = getDealStats(closed, false, isWeighted);

  const wonStats = getDealStats(wonOpportunitiesWithUntrackedOpportunities, false, isWeighted);

  const newStats = getDealStats(newOpportunities, false, isWeighted);

  return {
    stats: values(indexedStats),
    slippedOpportunities,
    currentOpportunities: currentOpportunitiesWithUntrackedOpportunities,
    lostOpportunities: lostOpportunitiesWithUntrackedOpportunities,
    wonOpportunities: wonOpportunitiesWithUntrackedOpportunities,
    noDecisionOpportunities: noDecisionOpportunitiesWithUntrackedOpportunities,
    closedOpportunities,
    // closedStats,
    noDecisionStats,
    newOpportunities,
    beginningStats,
    currentStats,
    lostStats,
    slippedStats,
    wonStats,
    newStats,
    forecasts,
    ptdWins,
    useForecastAccuracies,
    forecastAccuracies: newForecastAccuracies,
  };
}


function getAllPeriods(validStartDates = []) {
  const format = 'MMM YYYY';
  const seven = [];
  each([0, 1, 2, 3], (w) => {
    const s = moment().startOf('week').add(w, 'weeks');
    seven.push({
      label: `Week of ${s.format('MMM DD')}`,
      start: s,
      end: s.clone().endOf('week'),
    });
  });

  const thirty = [];
  each([0, 1, 2], (m) => {
    const s = moment().startOf('month').add(m, 'months');
    thirty.push({
      label: `${s.format(format)}`,
      start: s,
      end: s.clone().endOf('month'),
    });
  });

  const sixty = [
    {
      start: moment().startOf('month'),
      end: moment().startOf('month')
        .add(1, 'months')
        .endOf('month'),
    },
    {
      start: moment().subtract(1, 'months')
        .startOf('month'),
      end: moment().startOf('month')
        .endOf('month'),
    },
  ];
  const ninety = [
    {
      start: moment().startOf('month'),
      end: moment().startOf('month')
        .add(2, 'months')
        .endOf('month'),
    },
    {
      start: moment().subtract(1, 'months')
        .startOf('month'),
      end: moment().startOf('month')
        .add(1, 'months')
        .endOf('month'),
    },
    {
      start: moment().subtract(2, 'months')
        .startOf('month'),
      end: moment().startOf('month')
        .endOf('month'),
    },
  ];


  const periods = [
    {
      label: 'Weekly periods',
      ranges: seven,
    },
    {
      label: 'Monthly periods',
      ranges: thirty,
    },
    {
      label: '60 day periods',
      ranges: sixty,
    },
    {
      label: '90 day periods',
      ranges: ninety,
    },
  ];
  const ranges = [];
  each(periods, (period) => {
    const validItems = [];
    each(period.ranges, (item) => {
      if (!item.label) {
        item.label = `${item.start.format(format)} - ${item.end.format(format)}`;
      }
      if (!item.slug) {
        item.slug = item.label;
      }
      if (indexOf(validStartDates, item.start.format('YYYY-MM-DD')) >= 0) {
        validItems.push({
          ...item,
          start: item.start.format('YYYY-MM-DD'),
          end: item.end.format('YYYY-MM-DD'),
        });
      }
    });
    if (validItems.length) {
      ranges.push({ ...period, ranges: validItems });
    }
  });
  return ranges;
}

function getAllHistoryPeriods(validStartDates = [], min, max) {
  const format = 'MMM YYYY';
  const endOfMonth = moment().isSame(moment().endOf('month'), 'day') ? moment().endOf('month') : moment().subtract(1, 'months').endOf('month');
  const three = [
    {
      label: `${endOfMonth.clone().subtract(3 - 1, 'months')
        .startOf('month')
        .format(format)} - ${endOfMonth.format(format)}`,
      start: endOfMonth.clone().subtract(3 - 1, 'months').startOf('month'),
      end: endOfMonth.clone(),
    },
  ];
  const six = [
    {
      label: `${endOfMonth.clone().subtract(6 - 1, 'month')
        .startOf('month')
        .format(format)} - ${endOfMonth.format(format)}`,
      start: endOfMonth.clone().subtract(6 - 1, 'month').startOf('month'),
      end: endOfMonth.clone(),
    },
  ];
  const twelve = [
    {
      label: `${endOfMonth.clone().subtract(12 - 1, 'month')
        .startOf('month')
        .format(format)} - ${endOfMonth.format(format)}`,
      start: endOfMonth.clone().subtract(12 - 1, 'month').startOf('month'),
      end: endOfMonth.clone(),
    },
  ];

  const periods = [
    {
      label: 'Last 3 months',
      ranges: three,
    },
    {
      label: 'Last 6 months',
      ranges: six,
    },
    {
      label: 'Last 12 months',
      ranges: twelve,
    },
  ];
  const ranges = [];
  each(periods, (period) => {
    const validItems = [];
    each(period.ranges, (item) => {
      if (!item.label) {
        item.label = `${item.start.format(format)} - ${item.end.format(format)}`;
      }
      const valid = some(validStartDates, d => d.isBetween(item.start, item.end, null, '[]'));
      if (valid) {
        validItems.push({
          ...item,
          start: item.start.format('YYYY-MM-DD'),
          end: item.end.format('YYYY-MM-DD'),
        });
      }
    });
    if (validItems.length) {
      ranges.push({ ...period, ranges: validItems });
    }
  });
  const momentMin = min ? moment(min, 'YYYY-MM-DD') : null;
  const momentMax = max ? moment(max, 'YYYY-MM-DD') : null;
  if (momentMin && momentMin.isValid() && momentMax && momentMax.isValid && !momentMin.isSame(momentMax, 'day')) {
    ranges.push({
      label: 'All',
      ranges: [
        {
          label: `${momentMin.format(format)} -
            ${momentMax.format(format)}`,
          start: momentMin.clone()
            .startOf('month')
            .format('YYYY-MM-DD'),
          end: momentMax.clone()
            .endOf('month')
            .format('YYYY-MM-DD'),
        },
      ],
    });
  }
  return ranges;
}


const ForecastReducer = (state = initialState, action) => {
  switch (action.type) {
    case ADD_FORECAST_OPPORTUNITIES: {
      const {
        winRates, ptdWins, status, errors, forecasts, owners,
        forecastId, untrackedOpportunities, start, end, syncDate, isHistory,
        forecastAccuracies, isWeighted,
      } = action;
      let periodCurrent = 0;
      let periodTotal = 0;
      let periodRange = '';
      let periodStart = null;
      let periodEnd = null;
      let isFutureForecast = false;
      if (start && end) {
        periodStart = moment(start, 'YYYY-MM-DD');
        periodEnd = moment(end, 'YYYY-MM-DD');
        if (periodStart.isValid() && periodEnd.isValid() && periodEnd.isSameOrAfter(periodStart)) {
          periodCurrent = moment().diff(periodStart, 'days');
          periodTotal = periodEnd.diff(periodStart, 'days');
          if (periodStart.isSame(periodEnd, 'week')) {
            periodRange = `Week of ${periodStart.format('MMM DD')}`;
          } else if (periodStart.isSame(periodEnd, 'month')) {
            periodRange = periodStart.format('MMMM');
          } else {
            periodRange = `${periodStart.format('MMM \'YY')} - ${periodEnd.format('MMM \'YY')}`;
          }
          isFutureForecast = periodStart.isAfter(moment().startOf('day'), 'day');
        }
      }
      let syncDateMoment = null;
      if (syncDate) {
        const syncDateM = moment(syncDate, 'YYYY-MM-DD');
        if (syncDateM.isValid()) {
          syncDateMoment = syncDateM;
        }
      }
      return {
        ...state,
        winRates,
        ...transformData(forecasts, ptdWins, forecastAccuracies, owners, untrackedOpportunities, isHistory, isWeighted),
        status,
        errors,
        forecastId,
        periodCurrent,
        periodTotal,
        periodRange,
        periodStart,
        periodEnd,
        syncDate: syncDateMoment,
        isFutureForecast,
      };
    }
    case ADD_FORECAST_HISTORY_OPPORTUNITIES: {
      const {
        forecasts, selectedPeriod, status, errors,
      } = action;

      each(forecasts, (f) => {
        f._id = {
          start: moment(f._id.start, 'YYYY-MM-DD'),
          end: moment(f._id.end, 'YYYY-MM-DD'),
        };
        const longLabel = formatRange(f._id.start, f._id.end, selectedPeriod);

        let label = `${f._id.start.format('MMM')} - ${f._id.end.format('MMM')}`;
        if (selectedPeriod === 'month') {
          label = f._id.start.format('MMM YYYY');
        } else if (selectedPeriod === 'week') {
          label = f._id.start.format('MMM DD');
        }

        const begining = f.beginning_amount || 0;
        const won = (f.closed_won_amount || 0) + (f.new_won_amount || 0);
        const accuracy = f.beginning_amount > 0 ? won / begining : 0;

        f.label = label;
        f.longLabel = longLabel;
        f.accuracy = accuracy;
      });
      return {
        ...state,
        historyForecasts: forecasts,
        selectedPeriod,
        historyStatus: status,
        historyErrors: errors,
      };
    }
    case FETCH_FORECAST_PERIODS: {
      const all = action.params ? action.params.all : false;
      if (all) {
        const validPeriods = map(action.periods, period => moment(period, 'YYYY-MM-DD'));
        return { ...state, periods: getAllHistoryPeriods(validPeriods, action.min, action.max) };
      }
      return { ...state, periods: getAllPeriods(action.periods) };
    }
    case ADD_FORECAST_OPPORTUNITY_LOGS: {
      const collections = ['forecasts', 'currentOpportunities', 'slippedOpportunities', 'newOpportunities',
        'wonOpportunities', 'lostOpportunities', 'noDecisionOpportunities', 'closedOpportunities'];
      const { opportunityId, status, logDetails } = action;
      let newState = state;
      each(collections, (collection) => {
        const idx = findIndex(newState[collection], { opportunity_id: opportunityId });
        if (idx >= 0) {
          const newCollection = [...newState[collection]];
          newCollection[idx] = { ...newState[collection][idx], $status: status, $logDetails: logDetails };
          newState = { ...newState, [collection]: newCollection };
        }
      });
      return newState;
    }
    case DASHBOARD_FETCH_WIN_LOSS_OPPORTUNITY_REQUESTED_FEEDBACK: {
      const { total, id } = action;
      const fields = [
        'currentOpportunities',
        'forecasts',
        'lostOpportunities',
        'newOpportunities',
        'noDecisionOpportunities',
        'slippedOpportunities',
        'wonOpportunities',
        'closedOpportunities',
      ];
      const newState = {
        ...state,
      };
      each(fields, (field) => {
        newState[field] = reduce(newState[field], (items, opportunity) => {
          if (opportunity.opportunity_id === id) {
            items.push({
              ...opportunity,
              requested_feedbacks: total,
            });
          } else {
            items.push(opportunity);
          }
          return items;
        }, []);
      });
      return newState;
    }
    default:
      return state;
  }
};

/* Selectors */

// Get all posts
export const getForcasts = state => state.forecast.forecasts;
export const getForcastPeriods = state => state.forecast.periods;
export const getCurrentOpportunities = state => state.forecast.currentOpportunities;
export const getSlippedOpportunities = state => state.forecast.slippedOpportunities;
export const getLostOpportunities = state => state.forecast.lostOpportunities;
export const getWonOpportunities = state => state.forecast.wonOpportunities;
export const getNoDecisionOpportunities = state => state.forecast.noDecisionOpportunities;
export const getClosedOpportunities = state => state.forecast.closedOpportunities;
export const getNewOpportunities = state => state.forecast.newOpportunities;
export const getPeriodCurrent = state => state.forecast.periodCurrent;
export const getPeriodTotal = state => state.forecast.periodTotal;
export const getIsFutureForecast = state => state.forecast.isFutureForecast;
export const getPeriodRange = state => state.forecast.periodRange;
export const getPeriodStart = state => state.forecast.periodStart;
export const getSyncDate = state => state.forecast.syncDate;
export const getPeriodEnd = state => state.forecast.periodEnd;
export const getWinRates = state => state.forecast.winRates;
export const getStats = state => state.forecast.stats;

export const getBeginningStats = state => state.forecast.beginningStats;
export const getCurrentStats = state => state.forecast.currentStats;
export const getLostStats = state => state.forecast.lostStats;
export const getSlippedStats = state => state.forecast.slippedStats;
export const getWonStats = state => state.forecast.wonStats;
export const getNoDecisionStats = state => state.forecast.noDecisionStats;
export const getClosedStats = state => state.forecast.closedStats;

export const getNewStats = state => state.forecast.newStats;

export const getLoading = state => state.forecast.status === 'loading';
export const getLoaded = state => state.forecast.status === 'loaded';
export const getHasErrors = state => state.forecast.status === 'error';
export const getErrors = (state) => {
  if (state.forecast.status === 'error') {
    return state.forecast.errors;
  }
  return null;
};


export const getHistoryForecasts = state => state.forecast.historyForecasts;
export const getSelectedPeriod = state => state.forecast.selectedPeriod;
export const getHistoryLoading = state => state.forecast.historyStatus === 'loading';
export const getHistoryLoaded = state => state.forecast.historyStatus === 'loaded';
export const getHistoryHasErrors = state => state.forecast.historyStatus === 'error';
export const getForecastId = state => state.forecast.forecastId;
export const getHistoryErrors = (state) => {
  if (state.forecast.historyStatus === 'error') {
    return state.forecast.historyErrors;
  }
  return null;
};

export const getUseForecastAccuracies = state => state.forecast.useForecastAccuracies;
export const getForecastAccuracies = state => state.forecast.forecastAccuracies;


// Export Reducer
export default ForecastReducer;
